import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { KnowledgeBaseDocument, LibrarianIngestResult } from '@/components/knowledge/types';

interface LibrarianIngestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: KnowledgeBaseDocument[];
  onIngest: (document: KnowledgeBaseDocument) => Promise<LibrarianIngestResult | null>;
}

export default function LibrarianIngestDialog({ open, onOpenChange, documents, onIngest }: LibrarianIngestDialogProps) {
  const [selectedId, setSelectedId] = useState<string>('');
  const [result, setResult] = useState<LibrarianIngestResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const selectedDocument = useMemo(
    () => documents.find((document) => document.id === selectedId) ?? null,
    [documents, selectedId]
  );

  const handleIngest = async () => {
    if (!selectedDocument) return;
    setSubmitting(true);
    const nextResult = await onIngest(selectedDocument);
    setResult(nextResult);
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden border-border/70 bg-background/95 p-0 shadow-2xl sm:max-w-2xl">
        <DialogHeader className="border-b border-border/60 px-6 py-5">
          <DialogTitle>Process source with Librarian</DialogTitle>
          <DialogDescription>
            Pick a source document to turn into durable vault pages, index updates, and activity history.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 px-6 py-5">
          <div className="space-y-2">
            <p className="text-sm font-medium">Source document</p>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a source document" />
              </SelectTrigger>
              <SelectContent>
                {documents.map((document) => (
                  <SelectItem key={document.id} value={document.id}>
                    {document.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-xl border border-border/70 bg-muted/20 p-4 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">What happens during ingest</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Create or refresh the source summary page</li>
              <li>Update the vault index and activity log</li>
              <li>Refresh affected topic and entity pages</li>
            </ul>
          </div>

          {result && (
            <div className="rounded-xl border border-border/70 bg-card/70 p-4">
              <h4 className="font-medium">Ingest summary</h4>
              <div className="mt-3 grid gap-4 md:grid-cols-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Created</div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {result.createdPages.length > 0 ? result.createdPages.map((page) => <li key={page.id}>{page.path}</li>) : <li>None</li>}
                  </ul>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Updated</div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {result.updatedPages.length > 0 ? result.updatedPages.map((page) => <li key={page.id}>{page.path}</li>) : <li>None</li>}
                  </ul>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Issues</div>
                  <ul className="mt-2 space-y-1 text-sm">
                    {result.issues.length > 0 ? result.issues.map((issue) => <li key={issue}>{issue}</li>) : <li>None</li>}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-border/60 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button disabled={!selectedDocument || submitting} onClick={handleIngest}>
            {submitting ? 'Processing…' : 'Run ingest'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}